import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography
} from '@mui/material';
import _ from 'lodash';
import React, { useState } from 'react';
import { Controller, useController } from 'react-hook-form';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DateRangePicker from './DateRangePicker';
import DatePicker from './DatePicker';
import useMain from 'hooks/useMain';
import CloseIcon from '@mui/icons-material/Close';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { makeStyles } from '@mui/styles';
import { ErrorMessage } from '@hookform/error-message';

//checkbox round icon
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useTheme } from '@mui/material/styles';
import CusDateTimePicker from './DateTimePicker';

const formLabelColor = '#bfbfbf';
const OutlinedInputBorderRadius = '12px';

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    maxHeight: 200,
    borderRadius: OutlinedInputBorderRadius
  }
}));

const CusFormField = ({
  itemProps,
  label,
  options = [],
  isBoolean = false,
  labelProps,
  id,
  name,
  type = 'input',
  cusInput,
  fieldProps,
  cProps,
  control,
  hintText,
  showClear,
  checkboxTitle
}) => {
  const classes = useStyles();
  const { t } = useMain();
  const [showPw, setShowPw] = useState(false);
  const isMulti = type === 'multiSelect';
  const _label = label || t('PLS_SELECT');
  const _type = fieldProps?.disabled && !_.isEqual(type, 'checkbox') ? 'input' : type;
  const theme = useTheme();

  const renderSelect = React.forwardRef((props, ref) => {
    const checkValue = fieldProps?.multiple ? !_.isEmpty(props?.value) : props?.value;
    return (
      <>
        {/* {fieldProps?.placeholder && !props.value && (
          <InputLabel id={`${id}-label`} shrink={false} htmlFor={id}>
            {t('PLS_SELECT')}
          </InputLabel>
        )} */}
        <Select
          labelId={`${id}-label`}
          multiple={isMulti}
          fullWidth
          MenuProps={{ classes: { paper: classes.menuPaper } }}
          ref={ref}
          defaultValue=""
          {...props}
          endAdornment={
            <IconButton
              sx={{ display: checkValue ? '' : 'none', mr: 2 }}
              onClick={(e) => {
                e.target.value = '';
                props.onChange(e);
                if (props?.onClear) props.onClear();
              }}
            >
              <CloseIcon />
            </IconButton>
          }
          sx={{ borderRadius: OutlinedInputBorderRadius }}
        >
          {options.map((option) => (
            <MenuItem
              sx={{ ':hover': { backgroundColor: theme.palette.primary.lighter }, borderRadius: '10px' }}
              key={option.value}
              value={option.value}
              hidden={option?.hidden}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </>
    );
  });
  const renderCombobox = React.forwardRef(({ onChange, ...props }, ref) => {
    //
    // const defaultValue = _.find(options, ({ value }) => _.isEqual(value, props?.value));
    return (
      <Autocomplete
        // sx={{ '&.MuiOutlinedInput-root': { borderRadius: '14px' } }}
        fullWidth
        // multiple
        // limitTags={2}
        options={options ?? []}
        ref={ref}
        onChange={(e, v) => {
          onChange(v);
        }}
        getOptionLabel={(option) => option?.label ?? ''}
        {...props}
        // value={{
        //   label: '341800330102210573000051',
        //   value: '264cd97a-9812-46f0-a9c8-b1e5d48a8c64'
        // }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            sx={{
              '& .MuiAutocomplete-inputRoot': {
                borderRadius: OutlinedInputBorderRadius
              }
            }}
          />
        )}
      />
    );
  });

  const renderInput = React.forwardRef((props, ref) => {
    const checkValue = props?.value;
    const { formState: { errors } } = useController({
      name: props?.id,
      control
    })

    return (
      <>
        <OutlinedInput
          fullWidth
          type={!showPw && type === 'password' ? 'password' : 'text'}
          ref={ref}
          {...props}
          endAdornment={
            type === 'password' ? (
              <InputAdornment position="end">
                <IconButton aria-label="toggle password visibility" onClick={() => setShowPw(!showPw)} edge="end">
                  {showPw ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : showClear && props.value ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={(e) => {
                    e.target.value = '';
                    props.onChange(e);
                    if (props?.onClear) props.onClear();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : null
            //   (
            //   <InputAdornment position="end">
            //   <IconButton
            //       sx={{ display: checkValue ? '' : 'none' }}
            //       edge ="end"
            //       onClick={(e) => {
            //           e.target.value = '';
            //           props.onChange(e);
            //           if (props?.onClear) props.onClear();
            //       }}
            //   >
            //     <CloseIcon         //   </IconButton>
            // </InputAdornment>
            //   )
          }
          sx={{ borderRadius: OutlinedInputBorderRadius, borderColor: 'red !important' }}
        />
        <ErrorMessage
          errors={errors}
          name={props?.id}
          render={({ message }) => <p style={{color: 'red'}}>{message}</p>}
        />
      </>
    );
  });

  const elements = {
    input: renderInput,
    password: renderInput,
    select: renderSelect,
    multiSelect: renderSelect,
    combobox: renderCombobox,
    datePicker: React.forwardRef((props, ref) => {
      return <DatePicker {...props} ref={ref} />;
    }),
    dateTimePicker: CusDateTimePicker,
    dateRangePicker: React.forwardRef((props, ref) => {
      return <DateRangePicker {...props} ref={ref} />;
    }),
    checkbox: React.forwardRef((props, ref) => {
      // add checkboxTitle props
      return (
        <>
          {checkboxTitle && (
            <FormLabel id={`${id}-label`} htmlFor={id} sx={{ color: formLabelColor }} {...labelProps}>
              {checkboxTitle}
            </FormLabel>
          )}
          <FormControlLabel
            label={_label}
            control={
              <Checkbox
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleIcon />}
                sx={{ color: formLabelColor }}
                checked={!!props.value}
                {...props}
                ref={ref}
              />
            }
            labelPlacement="start"
            sx={{ marginLeft: '0px', justifyContent: 'start' }}
          />
        </>
      );
    })
  };

  const renderField = (field = {}) => {
    const intialValue = () => {
      switch (_type) {
        case 'multiSelect':
          return [];
        case 'datePicker':
        case 'dateTimePicker':
          return null;
        default:
          return '';
      }
    };
    const returnValue = () => {
      if (isBoolean) {
        return field?.value ? 'Y' : 'N';
      }
      if (fieldProps?.multiple) {
        return field?.value || [];
      }
      return field?.value || intialValue();
    };

    const controllerProps = _.isEmpty(field)
      ? {}
      : {
        ...field,
        onChange: (e) => {
          if (fieldProps?.onChange) {
            fieldProps?.onChange(e);
          }
          field?.onChange(e);
        },
        value: returnValue()
      };
    // const controllerProps = _.isEmpty(field)
    //     ? { ...field, ...defaultSelectMultipleProps() }
    //     : {
    //           ...field,
    //           ...defaultSelectMultipleProps(),
    //           value:
    //               fieldType === 'radio' ? fieldProps?.value : field.value,
    //           onChange: (e) => {
    //               if (fieldProps?.onChange) {
    //                   field?.onChange(fieldProps?.onChange(e))
    //               }
    //               field?.onChange(e)
    //           },
    //           onBlur: (e) => {
    //               if (fieldProps?.onBlur) {
    //                   fieldProps?.onBlur(e)
    //               }
    //               field?.onBlur(e)
    //           }
    //       }

    // if (cusInput) {
    //     return cusInput
    // }
    return React.createElement(elements[_type], {
      id,
      ...fieldProps,
      ...controllerProps,
      _type
    });
  };

  if (cusInput) {
    return cusInput;
  }

  return (
    <Grid item xs={12} sm={6} width="100%" {...itemProps}>
      <FormControl fullWidth>
        {_type !== 'checkbox' && (
          <>
            {/* revise inputlabel to formlabel */}
            {/* <InputLabel id={`${id}-label`} shrink htmlFor={id} {...labelProps}>
              {_label}
            </InputLabel> */}
            <FormLabel id={`${id}-label`} htmlFor={id} sx={{ color: formLabelColor }} {...labelProps}>
              {_label}
            </FormLabel>
          </>
        )}
        {control ? <Controller control={control} name={id} render={({ field }) => renderField(field)} {...cProps} /> : renderField()}
      </FormControl>
    </Grid>
  );
};

export default CusFormField;
